// Notification Service
// Handles real-time notifications using Firebase Cloud Messaging and Firestore

import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  doc,
  getDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: NotificationData;
  isRead: boolean;
  isArchived: boolean;
  priority: NotificationPriority;
  createdAt: Date;
  readAt?: Date;
  archivedAt?: Date;
  actionUrl?: string;
  actionText?: string;
  icon?: string;
  color?: string;
}

export interface NotificationData {
  postId?: string;
  subscriberId?: string;
  subscriptionId?: string;
  revenue?: number;
  [key: string]: any;
}

export type NotificationType = 
  | 'new_subscriber'
  | 'subscription_cancelled'
  | 'payment_received'
  | 'payment_failed'
  | 'post_published'
  | 'post_engagement'
  | 'system_update'
  | 'security_alert'
  | 'revenue_milestone'
  | 'content_recommendation'
  | 'automation_triggered'
  | 'moderation_required';

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

// Create a new notification
export const createNotification = async (
  userId: string,
  type: NotificationType,
  title: string,
  message: string,
  data?: NotificationData,
  priority: NotificationPriority = 'medium'
): Promise<string> => {
  try {
    const notification: Omit<Notification, 'id'> = {
      userId,
      type,
      title,
      message,
      data,
      isRead: false,
      isArchived: false,
      priority,
      createdAt: new Date(),
    };

    const docRef = await addDoc(collection(db, 'notifications'), {
      ...notification,
      createdAt: Timestamp.now(),
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Get notifications for a user
export const getNotifications = async (
  userId: string,
  limitCount: number = 50
): Promise<Notification[]> => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isArchived', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      readAt: doc.data().readAt?.toDate(),
      archivedAt: doc.data().archivedAt?.toDate(),
    })) as Notification[];
  } catch (error) {
    console.error('Error getting notifications:', error);
    return [];
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      isRead: true,
      readAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );

    const querySnapshot = await getDocs(q);
    const updatePromises = querySnapshot.docs.map(doc => 
      updateDoc(doc.ref, {
        isRead: true,
        readAt: Timestamp.now(),
      })
    );

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

// Archive notification
export const archiveNotification = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      isArchived: true,
      archivedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error archiving notification:', error);
    throw error;
  }
};

// Delete notification
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await deleteDoc(notificationRef);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

// Get unread notification count
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false),
      where('isArchived', '==', false)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread notification count:', error);
    return 0;
  }
};

// Subscribe to real-time notifications
export const subscribeToNotifications = (
  userId: string,
  callback: (notifications: Notification[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    where('isArchived', '==', false),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  return onSnapshot(q, (querySnapshot) => {
    const notifications = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      readAt: doc.data().readAt?.toDate(),
      archivedAt: doc.data().archivedAt?.toDate(),
    })) as Notification[];

    callback(notifications);
  });
};

// Subscribe to unread count
export const subscribeToUnreadCount = (
  userId: string,
  callback: (count: number) => void
): (() => void) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    where('isRead', '==', false),
    where('isArchived', '==', false)
  );

  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.size);
  });
};

// Notification templates
export const NotificationTemplates = {
  newSubscriber: (subscriberName: string, publicationName: string) => ({
    type: 'new_subscriber' as NotificationType,
    title: 'New Subscriber!',
    message: `${subscriberName} subscribed to ${publicationName}`,
    priority: 'medium' as NotificationPriority,
    icon: '👥',
    color: 'green',
    data: { subscriberName, publicationName },
  }),

  subscriptionCancelled: (subscriberName: string, publicationName: string) => ({
    type: 'subscription_cancelled' as NotificationType,
    title: 'Subscription Cancelled',
    message: `${subscriberName} cancelled their subscription to ${publicationName}`,
    priority: 'medium' as NotificationPriority,
    icon: '❌',
    color: 'red',
    data: { subscriberName, publicationName },
  }),

  paymentReceived: (amount: number, subscriberName: string) => ({
    type: 'payment_received' as NotificationType,
    title: 'Payment Received!',
    message: `$${amount} from ${subscriberName}`,
    priority: 'high' as NotificationPriority,
    icon: '💰',
    color: 'green',
    data: { revenue: amount, subscriberName },
  }),

  paymentFailed: (subscriberName: string, amount: number) => ({
    type: 'payment_failed' as NotificationType,
    title: 'Payment Failed',
    message: `Failed to charge $${amount} from ${subscriberName}`,
    priority: 'high' as NotificationPriority,
    icon: '⚠️',
    color: 'red',
    data: { subscriberName, amount },
  }),

  postPublished: (postTitle: string, subscriberCount: number) => ({
    type: 'post_published' as NotificationType,
    title: 'Post Published!',
    message: `"${postTitle}" sent to ${subscriberCount} subscribers`,
    priority: 'medium' as NotificationPriority,
    icon: '📧',
    color: 'blue',
    data: { postTitle, subscriberCount },
  }),

  postEngagement: (postTitle: string, engagementType: string, count: number) => ({
    type: 'post_engagement' as NotificationType,
    title: 'High Engagement!',
    message: `"${postTitle}" received ${count} ${engagementType}`,
    priority: 'medium' as NotificationPriority,
    icon: '🔥',
    color: 'orange',
    data: { postTitle, engagementType, count },
  }),

  revenueMilestone: (amount: number, milestone: string) => ({
    type: 'revenue_milestone' as NotificationType,
    title: 'Revenue Milestone!',
    message: `You've reached $${amount} - ${milestone}`,
    priority: 'high' as NotificationPriority,
    icon: '🎉',
    color: 'purple',
    data: { revenue: amount, milestone },
  }),

  systemUpdate: (updateTitle: string, updateDescription: string) => ({
    type: 'system_update' as NotificationType,
    title: updateTitle,
    message: updateDescription,
    priority: 'low' as NotificationPriority,
    icon: '🔧',
    color: 'blue',
    data: { updateTitle, updateDescription },
  }),

  securityAlert: (alertType: string, description: string) => ({
    type: 'security_alert' as NotificationType,
    title: 'Security Alert',
    message: `${alertType}: ${description}`,
    priority: 'urgent' as NotificationPriority,
    icon: '🔒',
    color: 'red',
    data: { alertType, description },
  }),

  contentRecommendation: (recommendation: string) => ({
    type: 'content_recommendation' as NotificationType,
    title: 'Content Suggestion',
    message: recommendation,
    priority: 'low' as NotificationPriority,
    icon: '💡',
    color: 'yellow',
    data: { recommendation },
  }),

  automationTriggered: (automationName: string, triggerCount: number) => ({
    type: 'automation_triggered' as NotificationType,
    title: 'Automation Triggered',
    message: `${automationName} activated for ${triggerCount} subscribers`,
    priority: 'medium' as NotificationPriority,
    icon: '🤖',
    color: 'blue',
    data: { automationName, triggerCount },
  }),

  moderationRequired: (contentType: string, contentTitle: string) => ({
    type: 'moderation_required' as NotificationType,
    title: 'Moderation Required',
    message: `${contentType}: "${contentTitle}" needs review`,
    priority: 'high' as NotificationPriority,
    icon: '🛡️',
    color: 'orange',
    data: { contentType, contentTitle },
  }),
};

// Auto-create notifications for common events
export const createAutoNotifications = {
  onNewSubscriber: async (userId: string, subscriberName: string, publicationName: string) => {
    const template = NotificationTemplates.newSubscriber(subscriberName, publicationName);
    return createNotification(userId, template.type, template.title, template.message, template.data, template.priority);
  },

  onSubscriptionCancelled: async (userId: string, subscriberName: string, publicationName: string) => {
    const template = NotificationTemplates.subscriptionCancelled(subscriberName, publicationName);
    return createNotification(userId, template.type, template.title, template.message, template.data, template.priority);
  },

  onPaymentReceived: async (userId: string, amount: number, subscriberName: string) => {
    const template = NotificationTemplates.paymentReceived(amount, subscriberName);
    return createNotification(userId, template.type, template.title, template.message, template.data, template.priority);
  },

  onPaymentFailed: async (userId: string, subscriberName: string, amount: number) => {
    const template = NotificationTemplates.paymentFailed(subscriberName, amount);
    return createNotification(userId, template.type, template.title, template.message, template.data, template.priority);
  },

  onPostPublished: async (userId: string, postTitle: string, subscriberCount: number) => {
    const template = NotificationTemplates.postPublished(postTitle, subscriberCount);
    return createNotification(userId, template.type, template.title, template.message, template.data, template.priority);
  },

  onRevenueMilestone: async (userId: string, amount: number, milestone: string) => {
    const template = NotificationTemplates.revenueMilestone(amount, milestone);
    return createNotification(userId, template.type, template.title, template.message, template.data, template.priority);
  },
};